import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { transportAPI, publicAPI, favoriteAPI } from '@/services/api';
import { MapPin, Users, Calendar, ShieldCheck } from 'lucide-react';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import ImageViewer from "@/components/ImageViewer";
import BackLink from "@/components/BackLink";
import LocationViewer from "@/components/LocationViewer";
import StickyInfoCard from "@/components/StickyInfoCard";

export default function TransportDetailPage() {
	const { id } = useParams();
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [transport, setTransport] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isFav, setIsFav] = useState(false);
	const [pricing, setPricing] = useState({
		free_images_limit: 5, price_extra_image: 0.70,
		free_videos_limit: 1, price_extra_video: 3.00,
		max_video_size_mb: 50, price_featured_ad: 10.00,
		vat_percent: 23
	});
	const API_BASE = process.env.REACT_APP_BACKEND_URL;

	useDocumentMeta({
		title: transport ? `${transport.title} | Equi-Market` : 'Equi-Market',
		description: transport?.description?.slice(0, 200),
		image: transport?.images?.[0] ? (transport.images[0].startsWith('http') ? transport.images[0] : `${process.env.REACT_APP_BACKEND_URL}${transport.images[0]}`) : '',
		url: transport ? `${window.location.origin}/transports/${transport.slug || transport.id}` : '',
		price: transport?.price,
		currency: 'EUR',
		type: 'transport',
	});

	useEffect(() => {
		setLoading(true);
		publicAPI.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		transportAPI.get(id).then(r => {
			setTransport(r.data);
			if (user) {
				favoriteAPI.check(r.data.id).then(f => setIsFav(f.data.is_favorite))
					.catch(() => {});
			}
		}).catch(() => navigate('/transports')).finally(() => setLoading(false));
	}, [id, user, navigate]);

	if (loading || !transport) {
		return <div className="pt-24 min-h-screen flex items-center justify-center text-[#666055]">Loading...</div>;
	}

	const hiddenImages = transport.hidden_images || [];
	const images = (transport.images || []).filter(url => !hiddenImages.includes(url)).map((url, i) => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		thumbnail: transport.thumbnails?.[i] ? (transport.thumbnails[i].startsWith('/') ? `${ API_BASE }${ transport.thumbnails[i] }` : transport.thumbnails[i]) : null
	}));
	const hasLocation = transport.location_lat && transport.location_lng;
	const services = transport.services || [];

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="transport-detail-page">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
				{/* Breadcrumb */}
				<BackLink title={transport.title} uri="/transports" />

				{/* Left: details */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<div className="lg:col-span-2 space-y-6">
						<ImageViewer images={images} title={transport.title} is_featured={transport.is_featured} />

						<div className="flex flex-wrap gap-4 text-sm text-[#3a3a3a] pt-3 border-t border-[#E6E2D6]">
							{transport.capacity > 0 && (
								<span className="inline-flex items-center gap-1.5">
								<Users className="w-4 h-4 text-[#8B4513]" />
									{transport.capacity} {t('transport.horses')}
							</span>
							)}
							{transport.coverage_area && (
								<span className="inline-flex items-center gap-1.5">
								<MapPin className="w-4 h-4 text-[#8B4513]" />
									{transport.coverage_area}
							</span>
							)}
							{transport.company_name && (
								<span className="inline-flex items-center gap-1.5">
								<ShieldCheck className="w-4 h-4 text-[#8B4513]" />
									{transport.company_name}
							</span>
							)}
							{transport.license_number && (
								<span className="text-xs text-[#666055]">
								{t('transport.license')}: <strong>{transport.license_number}</strong>
							</span>
							)}
						</div>
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
							<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
								{t('horse.description')}
							</h2>
							<p className="text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-line">
								{transport.description}
							</p>
						</div>

						{services.length > 0 && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{t('transport.services')}
								</h2>
								<div className="flex flex-wrap gap-2">
									{services.map((s) => (
										<span key={s}
													className="text-xs bg-[#F2F0E9] text-[#1A3C34] px-3 py-1.5 rounded-sm border border-[#E6E2D6]"
													data-testid={`transport-service-${s}`}>
                      {t(`transport.service.${s}`, s)}
                    </span>
									))}
								</div>
							</div>
						)}

						{(transport.origin_name || transport.destination_name || transport.location_name) && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{t('transport.route')}
								</h2>
								<ul className="space-y-2 text-sm text-[#3a3a3a]">
									{transport.location_name && (
										<li className="flex items-center gap-2">
											<span className="text-[#666055]/70 text-xs w-20">{t('transport.base')}:</span>
											<MapPin className="w-3 h-3 text-[#8B4513]" /> {transport.location_name}
										</li>
									)}
									{transport.origin_name && (
										<li className="flex items-center gap-2">
											<span className="text-[#666055]/70 text-xs w-20">{t('transport.origin')}:</span>
											<MapPin className="w-3 h-3 text-[#8B4513]" /> {transport.origin_name}
										</li>
									)}
									{transport.destination_name && (
										<li className="flex items-center gap-2">
											<span className="text-[#666055]/70 text-xs w-20">{t('transport.destination')}:</span>
											<MapPin className="w-3 h-3 text-[#8B4513]" /> {transport.destination_name}
										</li>
									)}
								</ul>
							</div>
						)}

						{(transport.available_from || transport.available_until) && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3 inline-flex items-center gap-2">
									<Calendar className="w-3 h-3" /> {t('transport.availability')}
								</h2>
								<p className="text-sm text-[#3a3a3a]">
									{transport.available_from && transport.available_from.slice(0, 10)}
									{transport.available_from && transport.available_until && '→'}
									{transport.available_until && transport.available_until.slice(0, 10)}
								</p>
							</div>
						)}

						{/* Map */}
						{ hasLocation &&
							<LocationViewer title={transport.title}
															location_name={transport.location_name}
															location_lat={transport.location_lat}
															location_lng={transport.location_lng} />
						}
					</div>

					{/* Right - Sticky Info */}
					<StickyInfoCard ad={transport}
													adType="transport"
													is_favorite={isFav}
													pricing={pricing} />

				</div>
			</div>
		</div>
	);
}